import React from "react";

// Define the paths we want to remap into date strings
const STATIC_REMAP_PATHS = ["schedule.*.due"];

type DiffValue =
  | { type: "changed"; from: unknown; to: unknown }
  | { type: "added"; to: unknown }
  | { type: "removed"; from: unknown }
  | { type: "nested"; children: Record<string | number, DiffValue> };

function pathMatches(path: (string | number)[], remapPaths: string[]): boolean {
  for (let len = path.length; len > 0; len--) {
    const subPath = path.slice(0, len);
    const pathStr = subPath
      .map((p) => (typeof p === "number" ? "*" : String(p)))
      .join(".");
    if (
      remapPaths.some((remapPattern) => {
        const remapParts = remapPattern.split(".");
        const pathParts = pathStr.split(".");
        if (remapParts.length !== pathParts.length) return false;
        for (let i = 0; i < remapParts.length; i++) {
          if (remapParts[i] === "*") continue;
          if (remapParts[i] !== pathParts[i]) return false;
        }
        return true;
      })
    ) {
      return true;
    }
  }
  return false;
}

function remapObject(
  value: unknown,
  path: (string | number)[] = [],
  remapPaths: string[]
): unknown {
  if (pathMatches(path, remapPaths)) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return new Date(value * 1000).toISOString();
    }
  }
  if (Array.isArray(value)) {
    return value.map((v, i) => remapObject(v, [...path, i], remapPaths));
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const remapped: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      remapped[k] = remapObject(v, [...path, k], remapPaths);
    }
    return remapped;
  }
  return value;
}

function getRecursiveDiff(
  prev: any,
  curr: any,
  path: (string | number)[] = []
): DiffValue | undefined {
  if (Array.isArray(prev) && Array.isArray(curr)) {
    const maxLen = Math.max(prev.length, curr.length);
    const children: Record<number, DiffValue> = {};
    let changed = false;
    for (let i = 0; i < maxLen; i++) {
      if (i in prev && i in curr) {
        const child = getRecursiveDiff(prev[i], curr[i], [...path, i]);
        if (child) {
          children[i] = child;
          changed = true;
        }
      } else if (i in prev) {
        children[i] = { type: "removed", from: prev[i] };
        changed = true;
      } else if (i in curr) {
        children[i] = { type: "added", to: curr[i] };
        changed = true;
      }
    }
    return changed ? { type: "nested", children } : undefined;
  } else if (
    prev &&
    curr &&
    typeof prev === "object" &&
    typeof curr === "object" &&
    !Array.isArray(prev) &&
    !Array.isArray(curr)
  ) {
    const keys = new Set([...Object.keys(prev), ...Object.keys(curr)]);
    const children: Record<string, DiffValue> = {};
    let changed = false;
    for (const key of Array.from(keys)) {
      if (key in prev && key in curr) {
        const child = getRecursiveDiff(prev[key], curr[key], [...path, key]);
        if (child) {
          children[key] = child;
          changed = true;
        }
      } else if (key in prev) {
        children[key] = { type: "removed", from: prev[key] };
        changed = true;
      } else if (key in curr) {
        children[key] = { type: "added", to: curr[key] };
        changed = true;
      }
    }
    return changed ? { type: "nested", children } : undefined;
  } else if (prev !== curr) {
    if (prev === undefined) return { type: "added", to: curr };
    if (curr === undefined) return { type: "removed", from: prev };
    return {
      type: "changed",
      from: prev,
      to: curr,
    };
  }
  return undefined;
}

function renderDiff(
  diff: DiffValue,
  keyPrefix = "",
  path: (string | number)[] = [],
  remapPaths: string[] = []
): React.ReactNode {
  if (diff.type === "changed") {
    return (
      <span>
        <span className="text-red-600 line-through">
          {JSON.stringify(diff.from)}
        </span>{" "}
        <span className="text-green-700">→ {JSON.stringify(diff.to)}</span>
      </span>
    );
  }
  if (diff.type === "added") {
    return <span className="text-green-700">+ {JSON.stringify(diff.to)}</span>;
  }
  if (diff.type === "removed") {
    return (
      <span className="text-red-600 line-through">
        - {JSON.stringify(diff.from)}
      </span>
    );
  }
  if (diff.type === "nested") {
    return (
      <ul className="ml-4 border-l border-gray-200 pl-2">
        {Object.entries(diff.children).map(([k, v]) => (
          <li key={keyPrefix + k}>
            <span className="font-mono font-semibold">{k}:</span>{" "}
            {renderDiff(v, keyPrefix + k + ".", [...path, k], remapPaths)}
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

const AssignmentDiffViewer: React.FC<{
  prev: Record<string, unknown> | null;
  curr: Record<string, unknown>;
}> = ({ prev, curr }) => {
  if (!prev) {
    return (
      <div
        className="flex h-full min-h-0 flex-col overflow-auto text-xs"
        style={{ minHeight: "1.5em" }}
      ></div>
    );
  }
  const remappedPrev = remapObject(prev, [], STATIC_REMAP_PATHS);
  const remappedCurr = remapObject(curr, [], STATIC_REMAP_PATHS);
  const diff = getRecursiveDiff(remappedPrev, remappedCurr, []);
  if (!diff) {
    return <span className="h-full text-xs text-gray-400">No changes</span>;
  }
  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto text-xs">
      {renderDiff(diff)}
    </div>
  );
};

export default AssignmentDiffViewer;
